/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * TK-31d (22/10/2026) — LỚP ĐÈN NEMA trên bản đồ + chế độ SỬA VỊ TRÍ, theo `_BUS/thiet_ke/TK-31_32/BanDo.dc.html`
 * (bản 2) và chốt mục 7 TK-31 (vị trí hiển thị = `pos`, GPS không tự dời đèn).
 *
 *   · Hiện từ zoom ≥ 16 (hoặc tủ được chọn `?tu=`): mỗi đèn một chấm, MÀU THEO TRẠNG THÁI (badge máy chủ) hoặc
 *     THEO NHÓM (công tắc). Bấm chấm → thẻ: tên, trạng thái, P, dim, "Mở đèn". (TK-35: bỏ "Về vị trí GPS" — bản đồ chỉ dùng vị trí xếp tay.)
 *   · Đèn chưa có `pos`: gom thành nhãn "+N chưa có vị trí" cạnh tủ (không vẽ chấm).
 *   · "Sửa vị trí đèn" (tick `quan_ly_tu`): chấm kéo được (`Marker draggable`). LƯU = một lời gọi cả lô
 *     `PUT /api/nema/vi-tri` (kèm `nhanh`); HỦY = trả lại. Ngoài chế độ sửa: `draggable = false`.
 *   · TK-45 §1 (lee 25/10): KHÔNG còn kéo thẻ lên bản đồ — hàng chờ (`HangChoDen`, giữ lâu để kéo), thanh Lưu/Hủy đáy
 *     (`ThanhLuuDay`) và `lib/keoThe.ts` đã bỏ. Xếp đèn ở Cài đặt ban đầu `?xep=1`: chạm thẻ ⇒ chạm bản đồ; kéo chấm để chỉnh.
 *     Đường tuyến vẽ THEO NHÁNH (`lib/tuyenNema.ts`), màu theo nhánh (hai sắc của `--nhan`), "Màu theo nhóm" bật ⇒ theo nhóm.
 *   · Hộp lớp đèn (góc trên trái) thu thành MỘT NÚT, bấm mới mở (TK-34 treo); đang sửa thì mở sẵn.
 * Quy tắc 22/10: không mã đèn, không câu diễn giải; chú giải chỉ là màu + tên trạng thái.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import { tenNhomSo } from '../../lib/caiDat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import L from 'leaflet';
import { Marker, Polyline, Popup, useMap, useMapEvents } from 'react-leaflet';
import { api, errorMessage } from '../../lib/api';
import { CoQuyen, useAuth } from '../../lib/auth';
import { Badge, Button, useToast } from '../ui';
import { MAU_BADGE, NHAN_BADGE, khoaLenhDen, tenDen, type BadgeDen, type LyDoKhongNhan, nhanDenChon } from '../../lib/nemaNhan';
import { chuoiTheoNhanh, doanCuaChuoi, type Doan } from '../../lib/tuyenNema';
import { NutNhay } from './NutNhay';
import { iMa, lucCapNhat, pfHai } from '../../lib/vanHanh';
import { tuoiNgan } from '../../lib/nemaApi';

export interface DenBanDo { cabinetId: string; tenTu: string; addr: number; stt: number | null; name: string | null; groupId: string | null; badge: BadgeDen; giaLap: boolean | null; dim: number | null; p_w: number | null; pos: { lat: number; lng: number; src: string | null } | null; gps: { lat: number; lng: number } | null;
  /** TK-34: địa chỉ CHA trong mạng mesh (topo card); null = chưa có topology. */
  parent: number | null;
  /** TK-35: nhánh = ngõ ra cấp điện (null = chưa gán); capDien null = tủ mất kết nối (không biết). */
  nhanh: number | null; capDien: boolean | null;
  /** TK-46 §1: số nhóm THIẾT BỊ card đã xác nhận (null = chưa rõ nhóm) — đích của lệnh nhóm */
  nhomTb?: number | null;
  /** TK-44: máy chủ tính — số đo NULL khi không hiện hành; Dim / Nháy chỉ khi `nhanLenh` (lyDo = vì sao không) */
  soDoHienHanh?: boolean; nhanLenh?: boolean; lyDo?: LyDoKhongNhan | null;
  /** TK-37: tuyến của tủ (gắn ở `useDenNemaCacTuyen` từ truy vấn theo tuyến) — cờ `canControl` của nút Nháy. */
  tuyenId?: string;
  /** TK-38 việc 2e/2f: số đo (NULL ⇒ "—") — I đơn vị 0,01 A (web đổi ra mA qua `iMa`), PF % — + mạng + lúc đo */
  online?: boolean | null; u_v?: number | null; i_10ma?: number | null; pf_pct?: number | null; sensor_at?: string | null; last_seen_at?: string | null }
/* TK-44: soDen = đèn THUỘC HỆ THỐNG (0 ⇒ "Chưa cài đèn"); lyDoTraVe = tủ nhận "Trả về lịch" được không */
interface TuyenDen { tuyen: { id: string; name: string }; nhom: { id: string; name: string; color: string | null; so: number | null }[]; tu: { id: string; name: string; lat: number | null; lng: number | null; soDen: number; lyDoTraVe?: LyDoKhongNhan | null }[]; den: DenBanDo[] }

/* TK-34 lượt 2: 16 ⇒ 14 — mẫu V3 thấy đường tuyến + đèn từ khung một khu phố (zoom ~14). */
const ZOOM_HIEN = 14;
/* Màu chấm theo badge: `MAU_BADGE` ở `lib/nemaNhan.ts` (một bảng, cùng ngân sách màu với huy hiệu). */
const khoa = (d: { cabinetId: string; addr: number }) => `${d.cabinetId}:${d.addr}`;

export function useDenNemaCacTuyen(tuyenIds: string[]) {
  const qs = useQueries({ queries: tuyenIds.map((id) => ({ queryKey: ['nema', 'tuyen', id, 'den'] as const, queryFn: () => api.get<TuyenDen>(`/api/nema/tuyen/${id}/den`), refetchInterval: 10_000 })) });
  return useMemo(() => {
    const den: DenBanDo[] = []; const tu: TuyenDen['tu'] = []; const nhom: TuyenDen['nhom'] = [];
    for (const q of qs) if (q.data) { const tid = q.data.tuyen.id; den.push(...q.data.den.map((d) => ({ ...d, tuyenId: tid }))); tu.push(...q.data.tu); nhom.push(...q.data.nhom); }
    return { den, tu, nhom, dangTai: qs.some((q) => q.isLoading) };
  }, [qs]);
}

/** Thanh điều khiển lớp (ngoài bản đồ) + lớp chấm (trong bản đồ) dùng chung một trạng thái — qua `useLopNema()`. */
export function useLopNema(tuyenIds: string[]) {
  const dl = useDenNemaCacTuyen(tuyenIds);
  const [hien, setHien] = useState(true);
  const [theoNhom, setTheoNhom] = useState(false);
  const [sua, setSua] = useState(false);
  /* TK-35: tủ đang XẾP (chỉ đèn tủ này kéo được, hàng chờ của tủ này); null = sửa mọi tủ (nút trong hộp lớp). */
  const [tuSua, setTuSua] = useState<string | null>(null);
  const [doi, setDoi] = useState<Map<string, { lat: number; lng: number }>>(new Map());
  /* TK-35: nhánh chọn trên thẻ hàng chờ (khoá đèn ⇒ nhánh | null) — ghi cùng lượt Lưu */
  const [nhanhDoi, setNhanhDoi] = useState<Map<string, number | null>>(new Map());
  const qc = useQueryClient(); const toast = useToast();
  const luu = useMutation({
    mutationFn: () => api.put<{ daLuu: number }>('/api/nema/vi-tri', { den: [...doi.entries()].map(([k, p]) => { const [c, a] = k.split(':'); return { cabinetId: c, addr: Number(a), lat: p.lat, lng: p.lng, ...(nhanhDoi.has(k) ? { nhanh: nhanhDoi.get(k) ?? null } : {}) }; }) }),
    onSuccess: (r) => { toast.success(`Đã lưu ${r.daLuu} đèn`); setDoi(new Map()); setNhanhDoi(new Map()); setSua(false); setTuSua(null); void qc.invalidateQueries({ queryKey: ['nema'] }); },
    onError: (e) => toast.error('Không lưu được', errorMessage(e)),
  });
  const huy = () => { setDoi(new Map()); setNhanhDoi(new Map()); setSua(false); setTuSua(null); };
  /* TK-44 §4: KHÔNG còn đường bật chế độ sửa từ /ban-do (`batSua` bỏ) — xếp đèn ở Cài đặt ban đầu `?xep=1` */
  return { ...dl, hien, setHien, theoNhom, setTheoNhom, sua, setSua, tuSua, doi, setDoi, nhanhDoi, setNhanhDoi, luu, huy };
}
export type LopNemaState = ReturnType<typeof useLopNema>;

/* ───────────────────────── trong MapContainer ───────────────────────── */

export function LopDenNema({ st, tuChon, noiBat = null, onChonDen, bayNoiBat = true, duoiMuc }: {
  st: LopNemaState; tuChon: string | null;
  /** TK-38 việc 2f: đèn đang chọn ở bảng đèn tuyến (`cabinetId:addr`) ⇒ vòng sáng + bay tới */
  noiBat?: string | null;
  /** KA-49 §4.5: khoá đèn "đang đuổi mức" (lệnh dim chưa được đèn báo) — viền hổ phách quanh chấm */
  duoiMuc?: ReadonlySet<string>;
  /** Vận hành 24/10: có ⇒ bấm chấm gọi hàm này (trang vẽ ô thông số riêng), không mở popup Leaflet */
  onChonDen?: (k: string) => void;
  /** false ⇒ không bay tới đèn nổi bật (đèn được chọn ngay trên bản đồ — đã ở trong khung nhìn) */
  bayNoiBat?: boolean;
}) {
  const map = useMap();
  /* TK-38: zoom đọc THẲNG từ bản đồ mỗi lần vẽ — bản chép trong state bị kẹt ở mức cũ khi bản đồ tự bay (tủ số 1) mà lớp này
     không nhận được `zoomend` (đo: state 11, bản đồ 15 ⇒ lớp đèn ẩn). Sự kiện chỉ còn để báo "vẽ lại". */
  const [, setNhip] = useState(0);
  useMapEvents({ zoomend: () => setNhip((n) => n + 1), moveend: () => setNhip((n) => n + 1) });
  const zoom = map.getZoom();
  const nhomMau = useMemo(() => new Map(st.nhom.map((g) => [g.id, g.color ?? '#64748b'])), [st.nhom]);
  const tuToaDo = useMemo(() => new Map(st.tu.map((t) => [t.id, t])), [st.tu]);
  const hienLop = st.hien && (zoom >= ZOOM_HIEN || tuChon !== null || st.sua);
  /* bay tới tủ được chọn một lần */
  const daBay = useRef<string | null>(null);
  useEffect(() => {
    if (!tuChon || daBay.current === tuChon) return;
    const t = tuToaDo.get(tuChon); if (t?.lat != null && t.lng != null) { map.setView([t.lat, t.lng], 17); daBay.current = tuChon; }
  }, [tuChon, tuToaDo, map]);
  const coPos = (d: DenBanDo) => st.doi.get(khoa(d)) ?? d.pos;
  const nhanhCua = (d: DenBanDo) => (st.nhanhDoi.has(khoa(d)) ? st.nhanhDoi.get(khoa(d)) ?? null : d.nhanh);
  /* TK-35 lượt 3: ĐƯỜNG TUYẾN theo NHÁNH (`lib/tuyenNema.ts`) — vị trí/nhánh đang hiện (kể cả đang kéo, chưa lưu). Mỗi đoạn tô
   * theo nhánh (hai sắc của --nhan) hoặc theo NHÓM của đèn ở đầu cuối khi "Màu theo nhóm" bật. */
  const duong = useMemo(() => {
    const chuoi = chuoiTheoNhanh(st.tu, st.den.map((d) => ({ ...d, nhanh: nhanhCua(d) })), (d) => coPos(d as DenBanDo));
    const theoMau = new Map<string, { lop: string | null; mau: string | null; doan: Doan[] }>();
    for (const c of chuoi) for (const { doan, addr } of doanCuaChuoi(c)) {
      const den = st.den.find((x) => x.cabinetId === c.cabinetId && x.addr === addr);
      const k = st.theoNhom ? `nhom:${den?.groupId ?? ''}` : `nhanh:${c.nhanh}`;
      const g = theoMau.get(k) ?? { lop: st.theoNhom ? null : c.nhanh === 1 ? '[stroke:rgb(var(--nhan))]' : '[stroke:rgb(var(--nhan-dam))]', mau: st.theoNhom ? (den?.groupId ? nhomMau.get(den.groupId) ?? '#64748b' : '#64748b') : null, doan: [] };
      g.doan.push(doan); theoMau.set(k, g);
    }
    return [...theoMau.entries()].map(([k, g]) => ({ k, ...g }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st.tu, st.den, st.doi, st.nhanhDoi, st.theoNhom, nhomMau]);
  /* TK-38 việc 2f: bấm dòng bảng đèn ⇒ bay tới chấm ấy (một lần mỗi lần chọn) */
  const daBayDen = useRef<string | null>(null);
  const denNoiBat = noiBat ? st.den.find((d) => khoa(d) === noiBat) ?? null : null;
  const posNoiBat = denNoiBat ? coPos(denNoiBat) : null;
  useEffect(() => {
    if (!noiBat || !posNoiBat || daBayDen.current === noiBat) return;
    if (!bayNoiBat) { daBayDen.current = noiBat; return; }
    map.setView([posNoiBat.lat, posNoiBat.lng], Math.max(map.getZoom(), 17)); daBayDen.current = noiBat;
  }, [noiBat, posNoiBat, map, bayNoiBat]);
  if (!hienLop && !noiBat) return null;
  /* chế độ xếp theo tủ: chỉ đèn của tủ ấy kéo được */
  const keoDuoc = (d: DenBanDo) => st.sua && (st.tuSua === null || d.cabinetId === st.tuSua);

  const theoTu = new Map<string, DenBanDo[]>();
  for (const d of st.den) { if (!coPos(d)) { const a = theoTu.get(d.cabinetId) ?? []; a.push(d); theoTu.set(d.cabinetId, a); } }

  return (
    <>
      {/* Hai lớp như V3: quầng rộng mờ + lõi mảnh. Màu nhánh = token (lớp CSS — thuộc tính SVG không đọc biến CSS); màu nhóm = mã nhóm. */}
      {duong.map((g) => (
        <Polyline key={`q-${g.k}`} positions={g.doan} interactive={false}
          pathOptions={{ weight: 8, opacity: 0.2, lineCap: 'round', color: g.mau ?? undefined, className: `duong-tuyen-quang ${g.lop ?? ''}` }} />
      ))}
      {duong.map((g) => (
        <Polyline key={`l-${g.k}`} positions={g.doan} interactive={false}
          pathOptions={{ weight: 2.2, opacity: 0.9, lineCap: 'round', color: g.mau ?? undefined, className: `duong-tuyen-loi ${g.lop ?? ''}` }} />
      ))}
      {st.den.filter((d) => coPos(d)).map((d) => {
        const p = coPos(d)!; const k = khoa(d);
        const mau = st.theoNhom ? (d.groupId ? nhomMau.get(d.groupId) ?? '#64748b' : '#64748b') : MAU_BADGE[d.badge];
        const daDoi = st.doi.has(k);
        /* TK-35: đèn vừa thả từ hàng chờ (chưa có pos đã lưu) ⇒ vị trí TẠM: chấm nét đứt */
        const tam = daDoi && !d.pos;
        /* TK-34 lượt 2: chấm 5 px + viền tối 1 px vẽ bằng bóng (không ăn vào 5 px) như V3, vùng bấm 14 px; chế độ SỬA giữ chấm 20 px để kéo được bằng tay. */
        /* TK-36: chế độ sửa — vùng chạm 44 px (người cài dùng ngón tay), chấm nhìn thấy vẫn 20 px ở giữa */
        const icon = st.sua ? L.divIcon({
          className: `cham-den cham-den-${d.badge}${tam ? ' cham-den-tam' : ''}`, iconSize: [44, 44], iconAnchor: [22, 22],
          html: `<span data-den-tam="${tam ? k : ''}" data-den-sua="${k}" style="display:block;width:20px;height:20px;margin:12px;box-sizing:border-box;border-radius:999px;background:${tam ? 'transparent' : mau};border:3px ${tam ? 'dashed' : 'solid'} ${tam ? mau : 'rgb(var(--nen-1))'};box-shadow:0 0 0 2px ${keoDuoc(d) ? '#f59e0b' : 'rgba(255,255,255,.25)'}${daDoi ? ',0 0 0 6px rgba(245,158,11,.3)' : ''}"></span>`,
        }) : L.divIcon({
          /* Vận hành (onChonDen): vùng bấm 18 px, nằm TRÊN tủ (zIndexOffset) — chấm nhìn thấy vẫn 5 px */
          className: `cham-den cham-den-${d.badge}`, iconSize: onChonDen ? [18, 18] : [14, 14], iconAnchor: onChonDen ? [9, 9] : [7, 7],
          html: `<span data-den-ban-do="${k}"${duoiMuc?.has(k) ? ' data-duoi-muc="1"' : ''} style="display:block;width:5px;height:5px;margin:${onChonDen ? 6.5 : 4.5}px;box-sizing:border-box;border-radius:999px;background:${mau};box-shadow:0 0 0 1px rgb(var(--nen-0))${duoiMuc?.has(k) ? ',0 0 0 3.5px rgb(var(--canh))' : ''}"></span>`,
        });
        const b = NHAN_BADGE[d.badge];
        return (
          <Marker key={k} position={[p.lat, p.lng]} icon={icon} draggable={keoDuoc(d)} zIndexOffset={onChonDen ? 1000 : 0}
            eventHandlers={{
              dragend: (e) => { const ll = (e.target as L.Marker).getLatLng(); st.setDoi(new Map(st.doi).set(k, { lat: ll.lat, lng: ll.lng })); },
              ...(onChonDen && !st.sua ? { click: () => onChonDen(k) } : {}),
            }}>
            {!st.sua && !onChonDen && (
              <Popup>
                <div className="space-y-1 text-sm" data-popup-den={k}>
                  <p className="m-0 flex items-center gap-2 font-semibold">{tenDen(d)} <Badge tone={b.tone}>{b.nhan}</Badge>{d.giaLap && <Badge tone="slate">GIẢ LẬP</Badge>}</p>
                  {/* TK-38 việc 2e: tên · tủ · nhánh · nhóm / U · I mA · P · PF · % / mạng · cập nhật / dim + trả về lịch + nháy */}
                  <p className="m-0 text-chu-2">{d.tenTu} · {d.nhanh == null ? 'chưa gán nhánh' : `Ngõ ${d.nhanh}`}{d.groupId ? ` · ${(() => { const g = st.nhom.find((x) => x.id === d.groupId); return g ? tenNhomSo(g) : 'nhóm'; })()}` : ''}</p>
                  <p className="m-0 tabular-nums text-chu-2" data-o="so-do-den">
                    {d.u_v == null ? '—' : `${d.u_v} V`} · {iMa(d.i_10ma) == null ? '—' : `${iMa(d.i_10ma)} mA`} · {d.p_w == null ? '—' : `${d.p_w} W`} · PF {pfHai(d.pf_pct) ?? '—'} · {d.dim == null ? '—' : `${d.dim} %`}
                  </p>
                  <p className={`m-0 ${d.online ? 'text-chu-2' : 'text-chu-3'}`}>{d.online == null ? '—' : d.online ? 'Trong mạng' : 'Mất mạng'} · {tuoiNgan(lucCapNhat(d))}</p>
                  <p className="m-0 flex flex-wrap items-center gap-3">
                    <Link to={`/tu/${d.cabinetId}?tab=nema`} className="text-nhan hover:underline">Mở đèn</Link>
                    <NutNhayTuyen d={d} />
                  </p>
                </div>
              </Popup>
            )}
          </Marker>
        );
      })}
      {/* TK-38 việc 2f: vòng sáng quanh đèn chọn ở bảng (không bấm được — chỉ chỉ chỗ) */}
      {posNoiBat && (
        <Marker position={[posNoiBat.lat, posNoiBat.lng]} interactive={false} zIndexOffset={2000}
          icon={L.divIcon({ className: 'den-noi-bat', iconSize: [30, 30], iconAnchor: [15, 15], html: `<span data-den-noi-bat="${noiBat}" style="display:block;width:30px;height:30px;border-radius:999px;border:3px solid rgb(var(--nhan-sang));box-shadow:0 0 0 4px rgb(var(--nhan) / .35)"></span>` })} />
      )}
      {/* KA-49 §4.5: nhãn "tên · mức % · P W" dưới vòng sáng của đèn đang chọn (không bấm được — không chặn chấm) */}
      {posNoiBat && denNoiBat && (
        <Marker position={[posNoiBat.lat, posNoiBat.lng]} interactive={false} zIndexOffset={2100}
          icon={L.divIcon({ className: 'nhan-den-chon', iconSize: [1, 1], iconAnchor: [0, -20], html: `<span data-o="nhan-den-chon">${nhanDenChon(denNoiBat, tenDen(denNoiBat))}</span>` })} />
      )}
      {/* nhãn gom "+N chưa có vị trí" cạnh tủ */}
      {[...theoTu.entries()].map(([cid, ds]) => {
        const t = tuToaDo.get(cid); if (!t || t.lat == null || t.lng == null) return null;
        const icon = L.divIcon({ className: 'nhan-gom-den', iconSize: [1, 1], iconAnchor: [-14, -8], html: `<span style="white-space:nowrap;font-size:12px;font-weight:600;color:#93c5fd;background:rgb(var(--nen-1) / .92);border:1px dashed rgba(147,197,253,.6);padding:3px 8px;border-radius:999px">+${ds.length} chưa có vị trí</span>` });
        return <Marker key={`gom-${cid}`} position={[t.lat, t.lng]} icon={icon} interactive={false} />;
      })}
    </>
  );
}

/* ───────────── hộp cài đặt hiển thị — TRÊN bản đồ, góc trên trái (`BanDoTu` prop `hopTrai`) ───────────── */

/**
 * TK-31 (lee 22/10): hộp chung cho Tổng quan và `/ban-do` — ô "Đèn", ô "Màu theo nhóm", chú giải (CHỈ các trạng thái
 *   đang có), và:
 *     · `/ban-do`: nút "Sửa vị trí đèn" (tick `quan_ly_tu`) + thanh sửa (Đặt / Lưu / Hủy) ngay trong hộp;
 *     · Tổng quan: LINK sang `/ban-do?tu=<tủ vừa bấm>` — sửa vị trí chỉ làm ở trang bản đồ.
 *   Thanh rời trên đầu trang `/ban-do` (bản trước) BỎ.
 */
export function HopLopNema({ st, cheDo, mapRef, tuChon }: {
  st: LopNemaState; cheDo: 'ban-do' | 'tong-quan';
  mapRef?: React.MutableRefObject<L.Map | null>; tuChon?: string | null;
}) {
  void mapRef;
  const dem = useMemo(() => { const m = new Map<BadgeDen, number>(); for (const d of st.den) m.set(d.badge, (m.get(d.badge) ?? 0) + 1); return m; }, [st.den]);
  /* TK-35 lượt 3 (TK-34 treo): hộp thu thành MỘT NÚT, bấm mới mở; đang sửa thì mở sẵn (thanh Lưu/Hủy phải thấy) */
  const [mo, setMo] = useState(false);
  if (!mo && !st.sua) {
    return (
      <button type="button" onClick={() => setMo(true)} aria-expanded={false} data-o="nut-lop-den"
        className="flex min-h-[28px] items-center gap-1.5 text-chu-1 hover:text-nhan">
        <span className="h-2.5 w-2.5 rounded-full bg-nhan-sang" aria-hidden="true" />Lớp đèn{!st.theoNhom && st.den.length > 0 && <span className="tabular-nums text-chu-3">{st.den.length}</span>}
      </button>
    );
  }
  return (
    <div className="flex max-w-[360px] flex-col gap-1.5" data-o="thanh-lop-nema" data-che-do={cheDo}>
      {!st.sua && <button type="button" onClick={() => setMo(false)} aria-expanded data-o="nut-lop-den" className="self-start text-xs text-chu-3 hover:text-chu-1">Lớp đèn ▴</button>}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* TK-33b: ở trang chính ô "Đèn" nằm trên THANH CÔNG CỤ bản đồ (mẫu) — cùng `st.hien`, không lặp ở đây. */}
        {cheDo !== 'tong-quan' && <label className="flex min-h-[28px] items-center gap-1.5 text-chu-1"><input type="checkbox" checked={st.hien} onChange={(e) => st.setHien(e.target.checked)} className="h-4 w-4 accent-nhan" data-o="o-den" />Đèn</label>}
        <label className="flex min-h-[28px] items-center gap-1.5 text-chu-1"><input type="checkbox" checked={st.theoNhom} onChange={(e) => st.setTheoNhom(e.target.checked)} className="h-4 w-4 accent-nhan" />Màu theo nhóm</label>
      </div>
      {!st.theoNhom && dem.size > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0" data-o="chu-giai-den">
          {(Object.keys(NHAN_BADGE) as BadgeDen[]).filter((b) => (dem.get(b) ?? 0) > 0).map((b) => (
            <li key={b} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: MAU_BADGE[b] }} />{NHAN_BADGE[b].nhan} <span className="tabular-nums text-chu-3">{dem.get(b)}</span></li>
          ))}
        </ul>
      )}
      {st.theoNhom && st.nhom.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0" data-o="chu-giai-nhom">
          {st.nhom.map((g) => <li key={g.id} className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: g.color ?? '#64748b' }} />{tenNhomSo(g)}</li>)}
        </ul>
      )}
      {/* TK-44 §4 (lee 24/10): KHÔNG còn "Sửa vị trí đèn" ở Vận hành / Bản đồ tuyến — xếp đèn là việc của Cài đặt ban đầu (`?xep=1`) */}
      {cheDo === 'ban-do' && st.sua && (
        <div role="status" className="flex flex-col gap-1.5 rounded-nut border border-canh/50 bg-canh-nen px-2.5 py-2" data-o="thanh-sua-vi-tri">
          <b className="text-canh">Kéo đèn tới đúng vị trí</b>
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-chu-1">{st.doi.size} đã dời</span>
            <Button size="sm" variant="primary" className="ml-auto" disabled={st.doi.size === 0 || st.luu.isPending} onClick={() => st.luu.mutate()}>Lưu</Button>
            <Button size="sm" variant="ghost" onClick={st.huy}>Hủy</Button>
          </span>
        </div>
      )}
    </div>
  );
}

/* TK-46 §1: dim một đèn KHÔNG còn ở popup — một chỗ duy nhất là khối "Điều khiển độ sáng" của Vận hành (phạm vi Đèn). */
/** TK-37: nút Nháy cho đèn trên bản đồ — quyết định cờ `canControl` theo TUYẾN của tủ (ba ca, không boolean). */
function NutNhayTuyen({ d }: { d: DenBanDo }) {
  const qd = useAuth().coCoTuyen('canControl', d.tuyenId ?? null);
  return <NutNhay cabinetId={d.cabinetId} addr={d.addr} ten={tenDen(d)} khoaVi={khoaLenhDen(d).dim} qd={qd} />;
}

/** Dòng thêm trong popup của tủ: "N đèn" (TK-44: ở lại trang — bấm tủ đã chọn tủ và mở khung tủ + bảng đèn; không link sang trang khác).
 *  Tủ không có đèn thuộc hệ thống ⇒ không vẽ gì. */
export function popupDemDen(st: LopNemaState) {
  return (cabinetId: string) => {
    const t = st.tu.find((x) => x.id === cabinetId);
    if (!t || t.soDen === 0) return null;
    return <p className="m-0 text-chu-2" data-o="popup-dem-den">{t.soDen} đèn</p>;
  };
}
